/**
 * Cart drawer header bootstrap — checkout, auto-discount sync, promo portal recovery.
 * Loads before theme upsell/checkout guards.
 */

let checkoutInProgress = false;
let discountSyncInFlight = null;

/** @returns {string} */
function getSettingsDiscountCode() {
  const code = typeof Theme !== 'undefined' ? Theme?.cartUpsell?.discountCode : '';
  return typeof code === 'string' ? code.trim() : '';
}

/** @returns {string[]} */
function getCartSectionIds() {
  /** @type {string[]} */
  const ids = [];
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      ids.push(el.dataset.sectionId);
    }
  });
  return ids;
}

/** @param {object} cart */
function getCartDiscountCodes(cart) {
  /** @type {Set<string>} */
  const codes = new Set();

  for (const app of cart.cart_level_discount_applications || []) {
    if (app.type === 'discount_code' && app.title) codes.add(app.title);
  }

  for (const item of cart.items || []) {
    for (const alloc of item.line_level_discount_allocations || []) {
      const app = alloc.discount_application;
      if (app?.type === 'discount_code' && app.title) codes.add(app.title);
    }
  }

  return [...codes];
}

/** @param {string} left @param {string} right */
function discountCodesMatch(left, right) {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

function clearPromoOpenState() {
  document.querySelectorAll('cart-drawer-component .cart-drawer__dialog').forEach((dialog) => {
    if (dialog instanceof HTMLElement) {
      dialog.removeAttribute('data-promo-open');
    }
  });
}

function reconcilePromoElements() {
  clearPromoOpenState();

  if (typeof window.__dcodedCartPromoReconcile === 'function') {
    requestAnimationFrame(() => {
      window.__dcodedCartPromoReconcile();
    });
    return;
  }

  requestAnimationFrame(() => {
    document.querySelectorAll('dcoded-cart-promo').forEach((promo) => {
      if (typeof promo._reconcileAfterCartMorph === 'function') {
        promo._reconcileAfterCartMorph();
      } else if (typeof promo._reconcilePortalNodes === 'function') {
        promo._reconcilePortalNodes();
        promo._ensurePortalNodes?.();
        promo._closeSheet?.();
      }
    });
  });
}

/** @param {object} data */
function morphCartFooters(data) {
  const sectionIds = getCartSectionIds();
  let updated = false;

  sectionIds.forEach((sectionId) => {
    const sectionHtml = data.sections?.[sectionId];
    if (!sectionHtml) return;

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const component = document.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);
    if (!component) return;

    const newFooter = doc.querySelector('.cart-drawer__footer');
    const oldFooter = component.querySelector('.cart-drawer__footer');
    if (newFooter && oldFooter) {
      oldFooter.replaceWith(newFooter);
      updated = true;
    }
  });

  if (updated) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.__dcodedCartPromoReconcile?.();
      });
    });
  }

  return updated;
}

async function dcodedApplyDiscountFallback() {
  const code = getSettingsDiscountCode();
  if (!code) return null;

  const sectionIds = getCartSectionIds();
  if (!sectionIds.length) return null;

  const cartResponse = await fetch('/cart.js', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!cartResponse.ok) return null;

  const cart = await cartResponse.json();
  if (!cart?.items?.length) return null;

  const existingCodes = getCartDiscountCodes(cart);
  if (existingCodes.some((existing) => discountCodesMatch(existing, code))) {
    return null;
  }

  const updateUrl =
    typeof Theme !== 'undefined' && Theme.routes?.cart_update_url
      ? Theme.routes.cart_update_url
      : '/cart/update.js';

  const response = await fetch(updateUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify({
      discount: [...existingCodes, code].join(','),
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    }),
  });

  if (!response.ok) return null;

  const data = await response.json();
  morphCartFooters(data);
  window.__bhCartUpsell?.refreshUpsellPricesFromCart?.(data);
  return data;
}

async function ensureDrawerDiscountSynced() {
  if (discountSyncInFlight) return discountSyncInFlight;

  discountSyncInFlight = (async () => {
    for (let attempt = 0; attempt < 20; attempt += 1) {
      if (window.__bhCartUpsell?.ensureDiscountSyncedAndRender) {
        try {
          await window.__bhCartUpsell.ensureDiscountSyncedAndRender();
        } catch (error) {
          console.error('Dcoded cart discount sync:', error);
        }
        reconcilePromoElements();
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    try {
      await dcodedApplyDiscountFallback();
    } catch (error) {
      console.error('Dcoded cart discount fallback:', error);
    }
  })().finally(() => {
    discountSyncInFlight = null;
  });

  return discountSyncInFlight;
}

async function proceedToCheckout() {
  if (checkoutInProgress) return;
  checkoutInProgress = true;

  try {
    await ensureDrawerDiscountSynced();
  } catch (error) {
    console.error('Dcoded checkout sync:', error);
  }

  window.location.assign('/checkout');
}

document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest(
      '#checkout, .cart__checkout-button[name="checkout"], .cart__checkout-button'
    );

    if (!trigger?.closest('.cart-drawer')) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    void proceedToCheckout();
  },
  true
);

document.addEventListener('dcoded-cart-drawer-refreshed', reconcilePromoElements);
document.addEventListener('cart:update', () => {
  requestAnimationFrame(reconcilePromoElements);
});

function bindDrawerWhenReady() {
  const drawer = document.querySelector('cart-drawer-component');
  if (!(drawer instanceof HTMLElement)) return;

  if (drawer.hasAttribute('data-dcoded-header-bound')) return;
  drawer.setAttribute('data-dcoded-header-bound', 'true');

  drawer.addEventListener('dialog:open', () => {
    void ensureDrawerDiscountSynced().then(() => {
      window.__dcodedCartPromoReconcile?.();
    });
  });

  const dialog = drawer.querySelector('dialog');
  if (dialog?.open) {
    void ensureDrawerDiscountSynced();
  }
}

function observeDrawerMount() {
  bindDrawerWhenReady();

  if (document.querySelector('cart-drawer-component')) return;

  const observer = new MutationObserver(() => {
    if (document.querySelector('cart-drawer-component')) {
      observer.disconnect();
      bindDrawerWhenReady();
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeDrawerMount);
} else {
  observeDrawerMount();
}
